import { iconNavFiltro } from '@bsds-lib-component/icons/icons';
import { BsdsIcon, BsdsInput } from '@bsds-lib-component/react';
import React from 'react';

import type { UseVidaTableListControlsReturn } from '../../../hooks/vida/useVidaTableListControls';
import type { DropdownOption } from '../../../types/componentType';
import Button from '../../button/Button';
import Dropdown from '../../dropdown/Dropdown';
import Visibility from '../../visibility/Visibility';

export type VidaTableListToolbarProps<T extends Record<string, string>> = {
  groupAriaLabel: string;
  searchPlaceholder: string;
  searchAriaLabel: string;
  sortId: string;
  sortOptions: DropdownOption[];
  filtersPanelAriaLabel: string;
  entityLabel: string;
  controls: UseVidaTableListControlsReturn<T>;
  children: React.ReactNode;
  showSearchClearInput?: boolean;
  className?: string;
};

const VidaTableListToolbar = <T extends Record<string, string>>({
  groupAriaLabel,
  searchPlaceholder,
  searchAriaLabel,
  sortId,
  sortOptions,
  filtersPanelAriaLabel,
  entityLabel,
  controls,
  children,
  showSearchClearInput = false,
  className = 'bsds-mb-16',
}: VidaTableListToolbarProps<T>) => {
  const {
    listSearchInputId,
    filtersPanelId,
    listSearchInputValue,
    handleSearchInput,
    sortBy,
    setSortBy,
    showFilters,
    toggleFilters,
    applyPanelFilters,
    clearPanelFilters,
    hasPanelFilterDraft,
    isPanelFilterActive,
    isPanelFilterApplyEnabled,
  } = controls;

  return (
    <div className={className}>
      <div
        className="flex flex-col sm:flex-row gap-4 w-full"
        role="group"
        aria-label={groupAriaLabel}
      >
        <div className="flex-1">
          <BsdsInput
            id={listSearchInputId}
            type="text"
            clearInput={showSearchClearInput}
            placeholder={searchPlaceholder}
            value={listSearchInputValue}
            onInput={handleSearchInput}
            platform="web"
            aria-label={searchAriaLabel}
          />
        </div>
        <div className="w-full sm:w-72">
          <Dropdown
            id={sortId}
            placeholder="Ordenar por..."
            value={sortBy}
            onChange={setSortBy}
            options={sortOptions}
          />
        </div>
        <Button
          type="button"
          color="primary"
          fill="outline"
          icon={iconNavFiltro}
          iconPosition="start"
          onClick={toggleFilters}
          aria-expanded={showFilters}
          aria-controls={filtersPanelId}
          aria-label="Abrir filtros da lista"
        >
          Filtros
        </Button>
      </div>

      <Visibility isActive={showFilters} animated animationDuration={300}>
        <div
          id={filtersPanelId}
          className="bsds-mt-16 bsds-width-100 bg-white rounded-lg shadow-sm bsds-p-24 border border-gray-100"
          role="region"
          aria-label={filtersPanelAriaLabel}
        >
          <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center bsds-mb-16">
            <BsdsIcon icon={iconNavFiltro} className="w-4 h-4" aria-hidden="true" />
            <h6 className="font-semibold bsds-ml-8">Filtros</h6>
          </div>
          <div className="bsds-display-flex flex-col gap-4">
            {children}
            <div className="bsds-display-flex flex-col sm:flex-row gap-4 bsds-align-items-end">
              <Button
                type="button"
                color="primary"
                fill="solid"
                disabled={!isPanelFilterApplyEnabled}
                className="bsds-width-100 sm:bsds-width-auto"
                onClick={applyPanelFilters}
                aria-label={
                  !isPanelFilterApplyEnabled
                    ? `Carregue a lista de ${entityLabel} antes de filtrar`
                    : 'Aplicar filtros na listagem carregada'
                }
              >
                Filtrar
              </Button>
              <Button
                type="button"
                color="secondary"
                fill="outline"
                disabled={!hasPanelFilterDraft && !isPanelFilterActive}
                className="bsds-width-100 sm:bsds-width-auto"
                onClick={clearPanelFilters}
                aria-label="Limpar filtros da listagem"
              >
                Limpar
              </Button>
            </div>
          </div>
        </div>
      </Visibility>
    </div>
  );
};

export default VidaTableListToolbar;
